import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, InputAdornment, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useActiveProject } from '../../../core/contexts/ProjectContext';
import { SharedProjectChip } from '../../../shared/components';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { LiveDot } from '../../../shared/components/LiveDot';
import { Pill } from '../../../shared/components/Pill';
import type { Project } from '../../../core/types/data-models';
import { ProjectMembersHeader } from '../ProjectMembersHeader';
import { ValidationVerdictChip } from './ValidationVerdictChip';

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

interface ProjectHeroProps {
  projectId: number;
  /** Undefined while the project is loading. */
  project: Project | undefined;
  loading: boolean;
  /**
   * True on the first-time "no documents yet" screen — hides the members,
   * original-prompt, and ask actions that don't apply until a project exists.
   */
  compact: boolean;
  canEdit: boolean;
  onOpenPrompt: () => void;
  onEdit: () => void;
}

/** Top panel of the project dashboard — name, status pills, greeting, header actions, and the "ask" bar. */
export function ProjectHero({ projectId, project, loading, compact, canEdit, onOpenPrompt, onEdit }: ProjectHeroProps) {
  const navigate = useNavigate();
  const { activeProject } = useActiveProject();
  const { user } = useAuth();

  // Hero "ask" bar — reuses the same search route as the header's search box.
  const [askQuery, setAskQuery] = useState('');
  const submitAskQuery = () => {
    const trimmed = askQuery.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setAskQuery('');
  };

  return (
    <GlassPanel sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          top: -96,
          right: -96,
          width: 384,
          height: 384,
          borderRadius: '50%',
          background: 'var(--color-primary-soft)',
          filter: 'blur(64px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -96,
          left: -96,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'var(--color-accent-cyan-soft)',
          filter: 'blur(64px)',
          pointerEvents: 'none',
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        sx={{ position: 'relative' }}
      >
        <Box sx={{ maxWidth: 640 }}>
          {loading ? (
            <>
              <Skeleton width={280} height={36} />
              <Skeleton width={420} height={20} sx={{ mt: 0.5 }} />
            </>
          ) : (
            <>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h5" fontWeight={800} letterSpacing="-0.01em">
                  {project?.name ?? `Project #${projectId}`}
                </Typography>
                <Pill color="success">
                  <LiveDot size={6} />
                  LIVE SYNCHRONIZED
                </Pill>
                {project &&
                  (project.projectType === 'IDEA_VALIDATION' ||
                    project.projectType === 'BUSINESS_VALIDATION' ||
                    project.projectType === 'STOCK_VALIDATION') &&
                  Number.isFinite(projectId) && (
                    <ValidationVerdictChip projectId={projectId} projectType={project.projectType} />
                  )}
                {activeProject?.access === 'shared' &&
                  activeProject.tenantName &&
                  activeProject.role && (
                    <SharedProjectChip
                      tenantName={activeProject.tenantName}
                      role={activeProject.role}
                      sharedBy={activeProject.sharedBy}
                    />
                  )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {greeting()}, {user?.name ?? user?.email ?? 'there'}
              </Typography>
              {project?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {project.description}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {!compact && Number.isFinite(projectId) && <ProjectMembersHeader projectId={projectId} />}
          {!compact && (
            <Tooltip title="View original prompt">
              <span>
                <IconButton size="small" onClick={onOpenPrompt} disabled={!project}>
                  <ArticleOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Edit project">
              <span>
                <IconButton size="small" onClick={onEdit} disabled={!project}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {!compact && (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          gap: 1.25,
          alignItems: 'center',
          mt: 3,
          pt: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={askQuery}
          onChange={(e) => setAskQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitAskQuery();
            }
          }}
          placeholder="Ask about your market, strategy or product — or any question…"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <AutoAwesomeIcon fontSize="small" color="primary" />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-bg)',
            },
          }}
        />
        <Button
          variant="contained"
          onClick={submitAskQuery}
          disabled={!askQuery.trim()}
          endIcon={<SearchIcon fontSize="small" />}
          sx={{ flexShrink: 0, fontWeight: 700 }}
        >
          Ask
        </Button>
      </Box>
      )}
    </GlassPanel>
  );
}
